import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { paymentStatus } from "@/lib/api";
import { CheckCircle, XCircle, Hourglass, Lightning } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const data = await paymentStatus(sessionId);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          data-testid={`payment-state-${state}`}
        >
          {state === "polling" && (
            <>
              <Hourglass weight="duotone" className="h-12 w-12 text-[#C9A961] mx-auto animate-pulse" />
              <h1 className="font-display text-3xl md:text-4xl font-light tracking-tighter mt-6">
                Confirming your <span className="font-black">payment</span>…
              </h1>
              <p className="text-[#9AA5B5] mt-3">
                This usually takes a few seconds. Don&apos;t close the tab.
              </p>
            </>
          )}

          {state === "paid" && (
            <>
              <CheckCircle weight="fill" className="h-14 w-14 text-[#10b981] mx-auto" />
              <h1 className="font-display text-3xl md:text-5xl font-light tracking-tighter mt-6">
                <span className="font-black gold-text">{info?.credits ?? "Your"}</span>{" "}
                credits added.
              </h1>
              <p className="text-[#9AA5B5] mt-4 max-w-md mx-auto">
                Your account has been topped up. Go forge another Irresistible
                Offer.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Button
                  onClick={() => navigate("/new")}
                  className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-8 py-5"
                  data-testid="payment-success-new-btn"
                >
                  <Lightning weight="fill" className="mr-2 h-4 w-4" />
                  Forge new offer
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="outline"
                  className="bg-transparent border-white/15 text-[#F5F1E8] hover:bg-white/5 rounded-sm px-8 py-5"
                  data-testid="payment-success-dashboard-btn"
                >
                  Go to dashboard
                </Button>
              </div>
            </>
          )}

          {(state === "expired" || state === "timeout" || state === "error") && (
            <>
              <XCircle weight="fill" className="h-12 w-12 text-[#ef4444] mx-auto" />
              <h1 className="font-display text-3xl font-light tracking-tighter mt-6">
                Payment{" "}
                <span className="font-black">
                  {state === "expired" ? "expired" : "could not be verified"}
                </span>
                .
              </h1>
              <p className="text-[#9AA5B5] mt-3">
                You were not charged. Try again from the pricing page.
              </p>
              <Button
                onClick={() => navigate("/pricing")}
                className="mt-8 bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-8 py-5"
                data-testid="payment-retry-btn"
              >
                Back to pricing
              </Button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
